/**
 * StatStrip — horizontal 4-up stats card per
 * `.claude/skills/sketch-findings-teachable_clone/references/components.md`
 * § StatStrip and `landing-philosophy.md` §5.
 *
 * On the marketing landing the 4 values are hardcoded JSX (no backend
 * dependency — landing-philosophy.md §5 explicitly rejects a
 * `/api/public/stats` endpoint). The component takes arbitrary strings
 * per item so a future post-auth caller (e.g. dashboard "your progress"
 * stats) can pass dynamic values without changing the component.
 *
 * Visual recipe:
 * - Container: bg-card surface, rounded-[40px] (= --radius-2xl), py-8,
 *   grid grid-cols-4 desktop, grid-cols-2 mobile.
 * - Each cell: text-center, px-4, right hairline border except last column
 *   and except mobile.
 * - Value: text-primary (coral), text-3xl, font-extrabold, block.
 * - Label: text-sm, text-muted-foreground, mt-1.
 */
import { type ReactNode } from "react";

export type StatStripProps = {
  children: ReactNode;
};

function StatStripRoot({ children }: StatStripProps) {
  return (
    <div className="grid grid-cols-2 gap-y-6 rounded-[40px] bg-card py-8 shadow-elev-1 md:grid-cols-4 md:gap-y-0">
      {children}
    </div>
  );
}

export type StatStripItemProps = {
  value: string;
  label: string;
};

function StatStripItem({ value, label }: StatStripItemProps) {
  return (
    <div className="border-r border-border px-4 text-center last:border-r-0 max-md:border-r-0">
      <span className="block text-3xl font-extrabold text-primary">
        {value}
      </span>
      <span className="mt-1 block text-sm text-muted-foreground">{label}</span>
    </div>
  );
}

export const StatStrip = Object.assign(StatStripRoot, { Item: StatStripItem });
